import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip";
import { 
  Users, Plus, Mail, Shield, Trash2, Check, X, Info, Loader2, Building2, Crown, UserPlus
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoTip = ({ text, side = "top" }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className="inline-flex items-center justify-center ml-1 text-zinc-400 hover:text-[#EAB308] transition-colors focus:outline-none">
        <Info size={14} />
      </button>
    </TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed bg-zinc-900 text-white border-none px-3 py-2">
      {text}
    </TooltipContent>
  </Tooltip>
);

const OrganizationView = () => {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);

  useEffect(() => { fetchOrg(); fetchPendingInvites(); }, []);

  const fetchOrg = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/organizations/mine`);
      setOrg(res.data.organization);
    } catch (e) {
      console.error("Failed to fetch org");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const res = await axios.get(`${API}/organizations/pending-invites`);
      setPendingInvites(res.data || []);
    } catch (e) { console.error("Failed to fetch invites"); }
  };

  const createOrg = async () => {
    if (!orgName.trim()) { toast.error("Organization name is required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/organizations`, { name: orgName });
      toast.success("Organization created!");
      setOrgName("");
      fetchOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create organization");
    } finally {
      setCreating(false);
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) { toast.error("Email is required"); return; }
    setInviting(true);
    try {
      await axios.post(`${API}/organizations/invite`, { email: inviteEmail, role: inviteRole });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      fetchOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const acceptInvite = async (orgId) => {
    try {
      await axios.post(`${API}/organizations/accept-invite/${orgId}`);
      toast.success("You've joined the organization!");
      fetchOrg();
      fetchPendingInvites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept invite");
    }
  };

  const removeMember = async (userId) => {
    try {
      await axios.delete(`${API}/organizations/members/${userId}`);
      toast.success("Member removed");
      fetchOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove member");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#EAB308]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="organization-view">
      <div>
        <h1 className="text-3xl font-black font-['Chivo'] uppercase tracking-tight flex items-center gap-2">
          Organization <InfoTip text="Manage your team. Org members share project visibility and can collaborate on contracts. Max 12 members on the Organization plan." />
        </h1>
        <p className="text-zinc-500 mt-1">Manage your team and shared project access</p>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="border-2 border-[#EAB308] bg-blue-50 p-5" data-testid="pending-invites">
          <h3 className="font-bold text-sm uppercase mb-3 text-[#EAB308]">Pending Invitations</h3>
          {pendingInvites.map(inv => (
            <div key={inv.invite_id} className="flex items-center justify-between p-3 bg-white border border-blue-200 mb-2">
              <div>
                <p className="font-medium text-sm">{inv.org_name}</p>
                <p className="text-xs text-zinc-500">Role: {inv.role} | Invited: {new Date(inv.invited_at).toLocaleDateString()}</p>
              </div>
              <Button onClick={() => acceptInvite(inv.org_id)} size="sm" className="bg-[#EAB308] text-white" data-testid={`btn-accept-invite-${inv.invite_id}`}>
                <Check size={14} className="mr-1" /> Accept
              </Button>
            </div>
          ))}
        </div>
      )}

      {!org ? (
        /* No org - Create one */
        <div className="border-2 border-dashed border-zinc-300 p-8 text-center" data-testid="create-org-section">
          <Building2 className="mx-auto mb-4 text-zinc-300" size={48} />
          <h3 className="font-bold text-lg mb-2">No Organization Yet</h3>
          <p className="text-zinc-500 text-sm mb-6">Create an organization to invite team members and share project access.</p>
          <div className="max-w-sm mx-auto space-y-3">
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Organization Name"
              className="w-full border-2 border-zinc-200 p-3 text-sm"
              data-testid="input-org-name"
            />
            <Button onClick={createOrg} disabled={creating} className="w-full bg-black text-white" data-testid="btn-create-org">
              {creating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
              Create Organization
            </Button>
          </div>
        </div>
      ) : (
        /* Org exists - Show details */
        <>
          <div className="border-2 border-black bg-zinc-900 text-white p-6" data-testid="org-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#EAB308] flex items-center justify-center text-white font-black text-xl">
                  {org.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{org.name}</h2>
                  <p className="text-zinc-400 text-sm">{org.members.length} / {org.max_users} members</p>
                </div>
              </div>
              <Badge className="bg-[#EAB308] text-white border-none text-xs">{org.members.length < org.max_users ? 'Active' : 'Full'}</Badge>
            </div>
          </div>

          {/* Invite Section */}
          <div className="border border-zinc-200 bg-white p-5" data-testid="invite-section">
            <h3 className="font-bold text-sm uppercase mb-3 flex items-center gap-1">
              <UserPlus size={16} /> Invite Member
              <InfoTip text="Invite team members by email. They'll see a pending invite when they log in and can accept to join your organization." />
            </h3>
            <div className="flex gap-3">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="flex-1 border-2 border-zinc-200 p-2 text-sm"
                data-testid="input-invite-email"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="border-2 border-zinc-200 p-2 text-sm"
                data-testid="select-invite-role"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <Button onClick={inviteMember} disabled={inviting} className="bg-[#EAB308] text-white" data-testid="btn-invite-member">
                {inviting ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} className="mr-1" />} Invite
              </Button>
            </div>
          </div>

          {/* Members List */}
          <div className="border border-zinc-200 bg-white" data-testid="members-list">
            <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <h3 className="font-bold text-sm uppercase">Members ({org.members.length})</h3>
              <Badge variant="outline" className="text-xs">{org.max_users - org.members.length} seats available</Badge>
            </div>
            <div className="divide-y divide-zinc-100">
              {org.members.map((m, i) => (
                <div key={i} className="p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors" data-testid={`member-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-200 flex items-center justify-center font-bold text-sm">
                      {(m.name || m.email).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm flex items-center gap-2">
                        {m.name || m.email}
                        {m.user_id === org.owner_id && <Crown size={14} className="text-amber-500" />}
                      </p>
                      <p className="text-xs text-zinc-500">{m.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-[10px]">
                      {m.role === 'admin' ? <><Shield size={10} className="mr-1" /> Admin</> : 'Member'}
                    </Badge>
                    {m.user_id !== org.owner_id && (
                      <button onClick={() => removeMember(m.user_id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`btn-remove-member-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pending Outgoing Invites */}
          {org.invites && org.invites.length > 0 && (
            <div className="border border-zinc-200 bg-white" data-testid="outgoing-invites">
              <div className="p-4 border-b border-zinc-200 bg-zinc-50">
                <h3 className="font-bold text-sm uppercase">Pending Invitations ({org.invites.length})</h3>
              </div>
              <div className="divide-y divide-zinc-100">
                {org.invites.map((inv, i) => (
                  <div key={i} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{inv.email}</p>
                      <p className="text-xs text-zinc-500">Role: {inv.role} | Sent: {new Date(inv.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Pending</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrganizationView;
